/**
 * MobileStyleManager - Platform-Scoped CSS Management
 * Ensures mobile CSS is properly applied only on mobile devices
 * Enterprise-grade mobile style management and optimization
 * CRITICAL: Provides separation of concerns - handles ONLY mobile CSS optimization
 */

// Dynamic import to avoid module loading issues
let MobileDetectionService;

export class MobileStyleManager {
    constructor() {
        this.appliedStyles = new Set();
        this.loadedStylesheets = new Set();
        this.mobileOptimizations = new Map();
        
        console.log('📱 MobileStyleManager initialized - SEPARATE from UnifiedMobileSystem');
        
        // CRITICAL: Immediate mobile detection and optimization
        // Don't wait for MobileDetectionService - optimize immediately if mobile
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        if (this.isMobile) {
            console.log('📱 Applying immediate mobile optimizations (fallback detection)');
            this.initializeMobileOptimizations();
        }
        
        // Then enhance with proper MobileDetectionService when available
        this.loadMobileDetectionService().then(() => {
            const properDetection = MobileDetectionService?.isMobile() || false;
            if (properDetection !== this.isMobile) {
                console.log(`🔄 Updating mobile detection: ${this.isMobile} → ${properDetection}`);
                this.isMobile = properDetection;
                if (this.isMobile && this.appliedStyles.size === 0) {
                    this.initializeMobileOptimizations();
                }
            }
        });
    }

    /**
     * Load MobileDetectionService dynamically
     */
    async loadMobileDetectionService() {
        try {
            if (window.MobileDetectionService) {
                MobileDetectionService = window.MobileDetectionService;
            } else {
                const mobileModule = await import('./MobileDetectionService.js');
                MobileDetectionService = mobileModule.default;
            }
        } catch (error) {
            console.warn('⚠️ MobileDetectionService not available:', error.message);
            // Fallback detection
            this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        }
    }

    /**
     * Initialize mobile-specific optimizations
     * CRITICAL: This is SEPARATE from UnifiedMobileSystem - handles CSS ONLY
     */
    initializeMobileOptimizations() {
        console.log('📱 Initializing mobile CSS optimizations (separate from UnifiedMobileSystem)');
        
        // Apply mobile-specific CSS optimizations that are SEPARATE from UnifiedMobileSystem
        this.applyMobileCSSOptimizations();
        
        // Setup viewport optimizations
        this.setupViewportOptimizations();
        
        // Apply iOS Safari specific fixes
        this.applyIOSFixes();
        
        // Setup touch optimizations
        this.setupTouchOptimizations();
        
        console.log('✅ Mobile CSS optimizations active (separate layer)');
    }

    /**
     * Apply mobile-specific CSS optimizations
     * CRITICAL: This provides CSS-specific mobile optimizations separate from UnifiedMobileSystem
     */
    applyMobileCSSOptimizations() {
        if (!this.isMobile) return;
        
        const mobileStyles = `
            /* MobileStyleManager - CSS-ONLY Mobile Optimizations */
            /* SEPARATE from UnifiedMobileSystem - handles CSS concerns only */
            
            /* CSS Variable Optimizations for Mobile */
            :root {
                --mobile-safe-area-top: env(safe-area-inset-top, 0px);
                --mobile-safe-area-bottom: env(safe-area-inset-bottom, 0px);
                --mobile-safe-area-left: env(safe-area-inset-left, 0px);
                --mobile-safe-area-right: env(safe-area-inset-right, 0px);
            }
            
            /* Mobile-specific CSS custom properties */
            .mobile-optimized {
                --mobile-font-scale: clamp(14px, 4vw, 18px);
                --mobile-spacing-scale: clamp(8px, 2vw, 16px);
                --mobile-button-height: max(44px, 10vw);
            }
            
            /* Mobile form optimizations - CSS layer only */
            @media screen and (max-width: 768px) {
                .auth-form-input, 
                .auth-form-select {
                    font-size: 16px !important; /* Prevent iOS zoom */
                    min-height: 44px !important; /* Touch target size */
                    touch-action: manipulation !important;
                }
                
                .auth-btn-back, 
                .auth-btn-next,
                .auth-btn-primary {
                    min-height: 44px !important;
                    font-size: 16px !important;
                    touch-action: manipulation !important;
                }
                
                /* Mobile accessibility improvements */
                *:focus-visible {
                    outline: 3px solid #00d4ff !important;
                    outline-offset: 2px !important;
                }
            }
            
            /* Mobile performance CSS optimizations */
            @media screen and (max-width: 768px) {
                .will-change-transform {
                    will-change: transform !important;
                }
                
                .gpu-accelerated {
                    transform: translateZ(0) !important;
                    backface-visibility: hidden !important;
                }
            }
        `;
        
        this.injectStyles('mobile-css-optimizations', mobileStyles);
        console.log('✅ Mobile CSS optimizations applied (separate from UnifiedMobileSystem)');
    }

    /**
     * Setup viewport optimizations
     */
    setupViewportOptimizations() {
        if (!this.isMobile) return;
        
        // CSS-specific viewport optimizations
        const viewportStyles = `
            @media screen and (max-width: 768px) {
                /* Mobile viewport optimizations */
                @viewport {
                    width: device-width;
                    initial-scale: 1.0;
                    maximum-scale: 1.0;
                    user-scalable: no;
                }
            }
        `;
        
        this.injectStyles('mobile-viewport-optimizations', viewportStyles);
        console.log('✅ Mobile viewport CSS optimized');
    }

    /**
     * Apply iOS Safari specific CSS fixes
     */
    applyIOSFixes() {
        if (!this.isMobile) return;
        
        // Detect iOS Safari
        const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        
        if (isIOSSafari) {
            const iosStyles = `
                /* iOS Safari CSS-specific fixes */
                @supports (-webkit-touch-callout: none) {
                    @media screen and (max-width: 768px) {
                        /* iOS-specific CSS optimizations */
                        body {
                            -webkit-text-size-adjust: 100% !important;
                        }
                        
                        input, textarea, select {
                            -webkit-appearance: none !important;
                            border-radius: 0 !important;
                        }
                        
                        /* Prevent iOS bounce on inputs */
                        .auth-form-input:focus {
                            position: fixed !important;
                        }
                    }
                }
            `;
            
            this.injectStyles('ios-safari-css-fixes', iosStyles);
            console.log('✅ iOS Safari CSS fixes applied');
        }
    }

    /**
     * Setup touch CSS optimizations
     */
    setupTouchOptimizations() {
        if (!this.isMobile) return;
        
        const touchStyles = `
            /* Mobile Touch CSS Optimizations */
            @media screen and (max-width: 768px) {
                /* Touch target optimizations */
                button,
                .button,
                .clickable {
                    min-height: 44px !important; /* iOS recommended touch target */
                    min-width: 44px !important;
                    touch-action: manipulation !important;
                }
                
                /* Prevent text selection on interactive elements */
                .no-select {
                    -webkit-user-select: none !important;
                    user-select: none !important;
                }
                
                /* Allow text selection in content areas */
                .selectable-text,
                input,
                textarea {
                    -webkit-user-select: text !important;
                    user-select: text !important;
                }
                
                /* Remove tap highlights */
                * {
                    -webkit-tap-highlight-color: transparent !important;
                }
                
                /* Focus states for accessibility */
                *:focus-visible {
                    outline: 2px solid #00d4ff !important;
                    outline-offset: 2px !important;
                }
            }
        `;
        
        this.injectStyles('mobile-touch-css-optimizations', touchStyles);
        console.log('✅ Mobile touch CSS optimizations applied');
    }

    /**
     * Inject styles with proper mobile scoping
     */
    injectStyles(id, css) {
        // Remove existing styles with same ID
        const existing = document.getElementById(id);
        if (existing) {
            existing.remove();
        }
        
        // Create and inject new styles with mobile-only media query
        const styleElement = document.createElement('style');
        styleElement.id = id;
        styleElement.media = 'screen and (max-width: 768px)'; // Mobile only
        styleElement.textContent = css;
        document.head.appendChild(styleElement);
        
        this.appliedStyles.add(id);
    }

    /**
     * Get mobile optimization status
     */
    getOptimizationStatus() {
        return {
            isMobile: this.isMobile,
            appliedStyles: Array.from(this.appliedStyles),
            loadedStylesheets: Array.from(this.loadedStylesheets),
            optimizedComponents: Array.from(this.mobileOptimizations.keys()),
            separateFromUnifiedMobile: true
        };
    }

    /**
     * Cleanup resources
     */
    cleanup() {
        // Remove applied styles
        this.appliedStyles.forEach(styleId => {
            const element = document.getElementById(styleId);
            if (element) {
                element.remove();
            }
        });
        
        this.appliedStyles.clear();
        this.loadedStylesheets.clear();
        this.mobileOptimizations.clear();
        
        console.log('🧹 MobileStyleManager cleaned up');
    }
}

// Create singleton instance
const mobileStyleManager = new MobileStyleManager();

// Make globally available for debugging
window.mobileStyleManager = mobileStyleManager;

export default mobileStyleManager;
